// Human-readable labels for feed surface display_type slugs. Sourced from
// DISPLAY_TYPE_OPTIONS (the canonical list backing the display type picker)
// so labels never drift out of sync with what operators can actually select.
import { DISPLAY_TYPE_OPTIONS } from '../components/feeds/displayTypeOptions'

const HYPHEN_OR_UNDERSCORE_RUN_PATTERN = /[_-]+/

export const DISPLAY_TYPE_LABELS = Object.fromEntries(
  DISPLAY_TYPE_OPTIONS.map((option) => [option.value, option.label])
)

// Slug -> human label overrides that a generic humanizeSlug() would get
// wrong (acronyms, punctuation, brand casing).
const SLUG_LABEL_OVERRIDES = {
  ...DISPLAY_TYPE_LABELS,
  kambi: 'Kambi',
  cryptodotcom: 'Crypto.com',
  og: 'OG',
}

/**
 * Human label for a surface's display_type slug. Falls back to a title-cased
 * rendering of the slug itself for any value not in DISPLAY_TYPE_OPTIONS, so
 * new/unrecognized display types never render as a raw snake_case string.
 */
export function labelForDisplayType(displayType) {
  if (!displayType) return ''
  return DISPLAY_TYPE_LABELS[displayType] || humanizeSlug(displayType)
}

// A segment of 2+ letters that's already all-caps (e.g. "PARXCASINO") is
// treated as an intentional acronym/brand and left untouched rather than
// title-cased into "Parxcasino".
const ALL_CAPS_SEGMENT = /^[A-Z]{2,}$/

/**
 * Generic snake_case/kebab-case slug -> Title Case humanizer, with overrides
 * for known display types and provider adapter names that don't title-case
 * cleanly (e.g. "cryptodotcom" -> "Crypto.com", not "Cryptodotcom"). Segments
 * that are already all-caps (e.g. "kambi_PARXCASINO" -> "Kambi PARXCASINO")
 * are preserved as-is.
 */
export function humanizeSlug(slug) {
  if (!slug) return ''
  const normalized = String(slug)
  if (SLUG_LABEL_OVERRIDES[normalized]) return SLUG_LABEL_OVERRIDES[normalized]

  return normalized
    .split(HYPHEN_OR_UNDERSCORE_RUN_PATTERN)
    .filter(Boolean)
    .map((segment) => (
      ALL_CAPS_SEGMENT.test(segment) ? segment : segment.charAt(0).toUpperCase() + segment.slice(1)
    ))
    .join(' ')
}
